import { useState, useCallback, useEffect, useMemo } from 'react';

/**
 * A generic hook to dynamically import a module.
 *
 * @param importFn A function that returns a dynamic import() Promise.
 * Example: () => import('some-library')
 * @returns An object containing:
 * - module: The imported module (or null if not yet loaded)
 * - isLoading: True if the module is currently loading
 * - error: Any error that occurred during loading
 * - promise: The Promise that resolves to the imported module
 */
export function useDynamicImport<T>(importFn: () => Promise<T>) {
  const [module, setModule] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Memoize the import function if it's passed directly as an arrow function
  // to prevent unnecessary re-runs of useEffect.
  // This is crucial if `importFn` is created inline in the component.
  const memoizedImportFn = useCallback(importFn, [importFn]);
  const promise = useMemo<Promise<T>>(() => memoizedImportFn(), [memoizedImportFn]);

  useEffect(() => {
    // Only load if the module hasn't been loaded yet
    // This handles cases where the hook might be mounted/unmounted multiple times
    // in different parts of the app, ensuring the actual import is only done once
    // and the cached module is returned for subsequent uses.

    if (module === null && !error) {
      promise
        .then(setModule)
        .catch((err) => {
          console.error('Failed to load module:', err);
          setError(err as Error);
        })
        .finally(() => setIsLoading(false));
    }
  }, [module, error, memoizedImportFn, promise]); // Dependencies for useEffect

  return { module, isLoading, error, promise };
}
